// src/pages/operations-dashboard/components/ShipmentStatusSection.jsx
import React, { useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import { LayoutGrid, TrendingUp } from "lucide-react";
import {
  STATUS_BUCKETS,
  formatCompact,
  formatPercent,
  formatShortDate,
  CHART_COLORS,
} from "../../../utils/dashboardConstants";

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-white/95 backdrop-blur-lg border border-gray-100 rounded-xl shadow-2xl px-4 py-3">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.payload.fill }} />
        <span className="text-sm font-semibold text-gray-800">{d.name}</span>
      </div>
      <p className="text-lg font-bold text-gray-900">{formatCompact(d.value)}</p>
      <p className="text-xs text-gray-400">{formatPercent(d.payload.percentage)}  of total</p>
    </div>
  );
};

const AreaTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-lg border border-gray-100 rounded-xl shadow-2xl px-4 py-3 max-w-xs">
      <p className="text-xs font-semibold text-gray-500 mb-2">{formatShortDate(label)}</p>
      <div className="space-y-1">
        {payload.filter((p) => p.value > 0).map((entry, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-xs text-gray-600">{entry.name}</span>
            </div>
            <span className="text-xs font-semibold text-gray-800">{formatCompact(entry.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Skeleton = ({ className = "" }) => (
  <div className={`bg-gray-100 rounded-lg animate-pulse ${className}`} />
);

export default function ShipmentStatusSection({ data, loading }) {
  const [activeView, setActiveView] = useState("donut");
  const isLoading = loading?.shipmentsStatus;

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <Skeleton className="h-6 w-48 mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Skeleton className="h-72" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const buckets = (data.status_bucket_counts || []).map((b) => {
    const config = STATUS_BUCKETS[b.bucket] || STATUS_BUCKETS.OTHER;
    return {
      name: b.bucket_display_name || config.label,
      value: b.count,
      percentage: b.percentage,
      fill: config.color,
      bucket: b.bucket,
    };
  });

  // Build stacked area chart data from daily_status_trends
  const trendData = (data.daily_status_trends || []).map((day) => {
    const entry = { date: day.date };
    (day.status_counts || []).forEach((sc) => {
      const config = STATUS_BUCKETS[sc.bucket] || STATUS_BUCKETS.OTHER;
      entry[config.label] = sc.count;
    });
    return entry;
  });

  const trendKeys = [...new Set(
    (data.daily_status_trends || [])
      .flatMap((day) => (day.status_counts || []).map((sc) => {
        const config = STATUS_BUCKETS[sc.bucket] || STATUS_BUCKETS.OTHER;
        return config.label;
      }))
  )];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-violet-50">
            <LayoutGrid className="w-4.5 h-4.5 text-violet-500" strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Overall Shipment Status</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Total: <span className="font-semibold text-gray-600">{formatCompact(data.total_shipments || 0)}</span> shipments
            </p>
          </div>
        </div>

        {/* View toggle */}
        <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setActiveView("donut")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              activeView === "donut" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Distribution
          </button>
          <button
            onClick={() => setActiveView("trend")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              activeView === "trend" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Trend
          </button>
        </div>
      </div>

      <div className="p-6">
        {activeView === "donut" ? (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Donut Chart */}
            <div className="lg:col-span-2 flex items-center justify-center">
              {buckets.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={buckets}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={110}
                      paddingAngle={2}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                      labelLine={false}
                      label={renderCustomizedLabel}
                    >
                      {buckets.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} stroke="white" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
                  No status data available
                </div>
              )}
            </div>

            {/* Status Buckets List */}
            <div className="lg:col-span-3 space-y-2">
              {buckets.map((bucket, i) => {
                const config = STATUS_BUCKETS[bucket.bucket] || STATUS_BUCKETS.OTHER;
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-3 p-3 rounded-xl border ${config.borderColor} ${config.bgLight} hover:shadow-sm transition-all`}
                  >
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: bucket.fill }} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${config.textColor}`}>{bucket.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">{formatCompact(bucket.value)}</p>
                      <p className="text-xs text-gray-400">{formatPercent(bucket.percentage)}</p>
                    </div>
                    {/* Mini progress bar */}
                    <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(bucket.percentage || 0, 100)}%`, backgroundColor: bucket.fill }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* Trend View */
          <div>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={trendData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                  <defs>
                    {trendKeys.map((key, i) => {
                      const color = CHART_COLORS[i % CHART_COLORS.length];
                      return (
                        <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatShortDate}
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={{ stroke: "#e2e8f0" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatCompact}
                  />
                  <Tooltip content={<AreaTooltip />} />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 11, paddingTop: 12 }}
                  />
                  {trendKeys.map((key, i) => {
                    const color = CHART_COLORS[i % CHART_COLORS.length];
                    return (
                      <Area
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={color}
                        strokeWidth={2}
                        fill={`url(#gradient-${key})`}
                        stackId="1"
                      />
                    );
                  })}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
                No trend data available for the selected period
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
